/* Ora Frames — text poster engine for the Milestone Frame and Couple Names Frame.
   window.OraTextPoster.draw(canvas, options) paints the poster at the canvas's size.
   Everything is laid out on a 1000 × 1414 page (ISO 1 : 1.414) and scaled, so the
   live preview and the 300 dpi print file come from the same drawing. */
(function () {
  'use strict';

  var PAGE_W = 1000, PAGE_H = 1414;

  // Backgrounds: two papers plus the star map's own sky colours (same gradients as ora-starmap-engine.js)
  var BACKGROUNDS = {
    ivory: { label: 'Ivory', colors: ['#FAF7F2', '#FAF7F2'], dark: false },
    white: { label: 'White', colors: ['#FFFFFF', '#FFFFFF'], dark: false },
    midnight: { label: 'Midnight Blue', colors: ['#39465C', '#131A2A'], dark: true },
    black: { label: 'Black', colors: ['#292C31', '#090D12'], dark: true },
    greyblue: { label: 'Grey Blue', colors: ['#5D6D8C', '#273143'], dark: true },
    aquamarine: { label: 'Aquamarine', colors: ['#396463', '#122B2D'], dark: true },
    purpledream: { label: 'Purple Dream', colors: ['#4A148C', '#12005E'], dark: true },
    sunset: { label: 'Sunset', colors: ['#FF512F', '#DD2476'], dark: true },
    emerald: { label: 'Emerald', colors: ['#0F2027', '#2C5364'], dark: true },
    deepocean: { label: 'Deep Ocean', colors: ['#000428', '#004e92'], dark: true }
  };

  var HEADERS = {
    starDots: 'Star and dots', moonStar: 'Moon and star', sparkle: 'Sparkle',
    heart: 'Heart', laurel: 'Laurel', constellation: 'Constellation', none: 'None'
  };

  // Name lettering choices (script and serif)
  // Lettering choices: the same seven as the star map, in the same order and with the same names
  var NAME_FONTS = {
    'Playfair Display': { label: 'Elegant', css: 'italic 400 {s}px "Playfair Display"', scale: 0.72 },
    'Spectral': { label: 'Classic', css: 'italic 400 {s}px "Spectral"', scale: 0.74 },
    'Great Vibes': { label: 'Grace', css: '400 {s}px "Great Vibes"', scale: 1 },
    'Poiret One': { label: 'Royal', css: '400 {s}px "Poiret One"', scale: 0.66 },
    'Imperial Script': { label: 'Imperial', css: '400 {s}px "Imperial Script"', scale: 1.02 },
    'Pinyon Script': { label: 'Pinyon', css: '400 {s}px "Pinyon Script"', scale: 0.86 },
    'Cormorant Garamond': { label: 'Garamond', css: 'italic 500 {s}px "Cormorant Garamond"', scale: 0.8 },
    'Noto Nastaliq Urdu': { label: 'Urdu', css: '400 {s}px "Noto Nastaliq Urdu"', scale: 0.6 }
  };

  var NUMBER_FONTS = {
    'Cormorant Garamond': { label: 'Classic', css: '500 {s}px "Cormorant Garamond"', scale: 1 },
    'Playfair Display': { label: 'Elegant', css: '400 {s}px "Playfair Display"', scale: 0.82 },
    'Jost': { label: 'Modern', css: '300 {s}px "Jost"', scale: 0.8 }
  };

  // Fonts the canvas needs before drawing
  var FONTS = [
    '500 40px "Cormorant Garamond"', 'italic 500 40px "Cormorant Garamond"', '400 40px "Jost"', '500 40px "Jost"', '300 40px "Jost"',
    '400 40px "Great Vibes"', '400 40px "Imperial Script"', '400 40px "Pinyon Script"',
    '400 40px "Playfair Display"', 'italic 400 40px "Playfair Display"', 'italic 400 40px "Spectral"', '400 40px "Poiret One"',
    '400 40px "Noto Nastaliq Urdu"'
  ];

  function font(spec, size) { return spec.replace('{s}', Math.round(size * 100) / 100); }

  function palette(o) {
    var bg = BACKGROUNDS[o.background] || BACKGROUNDS.ivory;
    var light = !bg.dark;
    var auto = light ? { ink: '#2B211B', muted: '#6B5D52', accent: '#BFA474', name: '#7A5C32', border: '#2B211B' }
                     : { ink: '#F6F1EA', muted: 'rgba(246,241,234,0.74)', accent: '#D9BC74', name: '#E9D3A1', border: 'rgba(246,241,234,0.9)' };
    if (o.accentColor) { auto.accent = o.accentColor; auto.name = o.accentColor; }
    return { bg: bg, c: auto };
  }

  // ---------- text helpers ----------
  function fitSize(ctx, text, spec, size, maxW, spacing) {
    ctx.font = font(spec, size);
    var w = spacedWidth(ctx, text, spacing ? spacing * size : 0);
    return w > maxW ? size * maxW / w : size;
  }
  function spacedWidth(ctx, text, spacing) {
    if (!spacing || /[؀-ۿݐ-ݿﭐ-﷿ﹰ-﻿]/.test(text)) return ctx.measureText(text).width;
    var w = 0; for (var i = 0; i < text.length; i++) w += ctx.measureText(text[i]).width + (i < text.length - 1 ? spacing : 0);
    return w;
  }
  // Centred text with optional letter spacing (drawn per character so every browser matches)
  function centred(ctx, text, cx, y, spacing) {
    // Urdu (or any Arabic-script) text: drawn whole and right to left so the letters stay joined
    if (/[؀-ۿݐ-ݿﭐ-﷿ﹰ-﻿]/.test(text)) { ctx.textAlign = 'center'; ctx.direction = 'rtl'; ctx.fillText(text, cx, y); ctx.direction = 'ltr'; return; }
    if (!spacing) { ctx.textAlign = 'center'; ctx.fillText(text, cx, y); return; }
    ctx.textAlign = 'left';
    var x = cx - spacedWidth(ctx, text, spacing) / 2;
    for (var i = 0; i < text.length; i++) { ctx.fillText(text[i], x, y); x += ctx.measureText(text[i]).width + spacing; }
  }
  function wrap(ctx, text, maxW, maxLines) {
    var words = String(text).split(/\s+/).filter(Boolean), lines = [], line = '';
    words.forEach(function (w) {
      var t = line ? line + ' ' + w : w;
      if (ctx.measureText(t).width <= maxW || !line) line = t; else { lines.push(line); line = w; }
    });
    if (line) lines.push(line);
    return lines.length > maxLines ? null : lines;
  }

  // ---------- ornaments (centred at cx, cy; drawn in page units) ----------
  function sparklePath(ctx, cx, cy, r, waist) {
    var w = r * (waist || 0.22);
    ctx.beginPath();
    ctx.moveTo(cx, cy - r);
    ctx.quadraticCurveTo(cx + w * 0.35, cy - w * 0.35, cx + r, cy);
    ctx.quadraticCurveTo(cx + w * 0.35, cy + w * 0.35, cx, cy + r);
    ctx.quadraticCurveTo(cx - w * 0.35, cy + w * 0.35, cx - r, cy);
    ctx.quadraticCurveTo(cx - w * 0.35, cy - w * 0.35, cx, cy - r);
    ctx.closePath();
  }
  function dot(ctx, x, y, r) { ctx.beginPath(); ctx.arc(x, y, r, 0, Math.PI * 2); ctx.fill(); }

  // The star map's own Moon and Heart shapes (sections/ora-star-map.liquid, Shape options)
  var MOON_PATH = 'M35.68,8.45 A17.1,17.1 0 1,0 35.68,41.55 A17.1,17.1 0 0,1 35.68,8.45 Z';
  var HEART_PATH = 'M25,7 C25,7 31,1 36,5 C41,9 41,15 37,21 C34,25 25,33 25,33 C25,33 16,25 13,21 C9,15 9,9 14,5 C19,1 25,7 25,7Z';
  // strokes an SVG path drawn in its own units, centred (ox, oy → cx, cy) and scaled by k
  function strokeShape(ctx, d, ox, oy, cx, cy, k, lineWidth) {
    ctx.save();
    ctx.translate(cx - ox * k, cy - oy * k); ctx.scale(k, k);
    ctx.lineWidth = lineWidth / k; ctx.lineJoin = 'round';
    ctx.stroke(new Path2D(d));
    ctx.restore();
  }

  var ORNAMENTS = {
    starDots: function (ctx, cx, cy, col) {
      ctx.fillStyle = col;
      sparklePath(ctx, cx, cy, 22); ctx.fill();
      dot(ctx, cx - 60, cy, 3.2); dot(ctx, cx + 60, cy, 3.2);
      dot(ctx, cx - 100, cy, 2.2); dot(ctx, cx + 100, cy, 2.2);
      return 46;
    },
    moonStar: function (ctx, cx, cy, col) {
      ctx.fillStyle = col; ctx.strokeStyle = col;
      strokeShape(ctx, MOON_PATH, 22, 25, cx - 18, cy, 2.5, 2.2);
      sparklePath(ctx, cx + 44, cy - 6, 15); ctx.fill();
      return 96;
    },
    sparkle: function (ctx, cx, cy, col) {
      ctx.fillStyle = col;
      sparklePath(ctx, cx, cy, 34, 0.2); ctx.fill();
      sparklePath(ctx, cx + 44, cy - 26, 10); ctx.fill();
      sparklePath(ctx, cx - 40, cy + 24, 7); ctx.fill();
      return 72;
    },
    heart: function (ctx, cx, cy, col) {
      ctx.strokeStyle = col; ctx.fillStyle = col;
      strokeShape(ctx, HEART_PATH, 25, 17.5, cx, cy, 2.1, 2.3);
      dot(ctx, cx - 70, cy + 2, 2.6); dot(ctx, cx + 70, cy + 2, 2.6);
      return 76;
    },
    laurel: function (ctx, cx, cy, col) {
      ctx.strokeStyle = col; ctx.fillStyle = col; ctx.lineWidth = 2;
      [-1, 1].forEach(function (side) {
        ctx.beginPath();
        ctx.moveTo(cx + side * 12, cy + 14);
        ctx.quadraticCurveTo(cx + side * 70, cy + 12, cx + side * 112, cy - 16);
        ctx.stroke();
        for (var i = 0; i < 5; i++) {
          var t = (i + 1) / 6;
          var x = cx + side * (12 + 100 * t), y = cy + 14 - 30 * t * t;
          [-1, 1].forEach(function (up) {
            ctx.save(); ctx.translate(x, y); ctx.rotate(side * (up < 0 ? -0.9 : 0.5) + (side < 0 ? Math.PI : 0));
            ctx.beginPath(); ctx.ellipse(9, 0, 10, 3.6, 0, 0, Math.PI * 2); ctx.fill(); ctx.restore();
          });
        }
      });
      dot(ctx, cx, cy + 14, 3.4);
      return 50;
    },
    constellation: function (ctx, cx, cy, col) {
      var pts = [[-96, 12], [-50, -14], [-8, 6], [34, -24], [78, -4], [104, 22]];
      ctx.strokeStyle = col; ctx.fillStyle = col; ctx.lineWidth = 1.4; ctx.globalAlpha = 0.85;
      ctx.beginPath(); pts.forEach(function (p, i) { (i ? ctx.lineTo : ctx.moveTo).call(ctx, cx + p[0], cy + p[1]); }); ctx.stroke();
      ctx.globalAlpha = 1;
      pts.forEach(function (p, i) { if (i === 3) { sparklePath(ctx, cx + p[0], cy + p[1], 12); ctx.fill(); } else dot(ctx, cx + p[0], cy + p[1], i % 2 ? 3.4 : 2.6); });
      return 60;
    },
    none: function () { return 0; }
  };

  // ---------- layout: a vertical stack of the visible parts, centred on the page ----------
  function stack(items, centreY) {
    items = items.filter(Boolean);
    var total = 0;
    items.forEach(function (it, i) { total += it.h + (i < items.length - 1 ? it.gap : 0); });
    var y = centreY - total / 2;
    items.forEach(function (it, i) { it.top = y; y += it.h + (i < items.length - 1 ? it.gap : 0); });
    return items;
  }

  function background(ctx, o, p) {
    var g = ctx.createLinearGradient(0, 0, PAGE_W, PAGE_H);
    g.addColorStop(0, p.bg.colors[0]); g.addColorStop(1, p.bg.colors[1]);
    ctx.fillStyle = g; ctx.fillRect(0, 0, PAGE_W, PAGE_H);
    if (o.border === 'double') {
      ctx.strokeStyle = p.c.border; ctx.lineWidth = 1.6; ctx.strokeRect(60, 60, PAGE_W - 120, PAGE_H - 120);
      ctx.strokeStyle = p.c.accent; ctx.lineWidth = 1.1; ctx.strokeRect(72, 72, PAGE_W - 144, PAGE_H - 144);
    } else if (o.border === 'single') {
      ctx.strokeStyle = p.c.accent; ctx.lineWidth = 1.4; ctx.strokeRect(60, 60, PAGE_W - 120, PAGE_H - 120);
    }
  }

  function rule(ctx, cy, col) { ctx.fillStyle = col; ctx.fillRect(PAGE_W / 2 - 40, cy - 0.7, 80, 1.4); }

  function milestone(ctx, o, p) {
    var c = p.c, cx = PAGE_W / 2, maxW = 760;
    var nf = NAME_FONTS[o.nameFont] || NAME_FONTS['Great Vibes'];
    var numf = NUMBER_FONTS[o.numberFont] || NUMBER_FONTS['Cormorant Garamond'];
    var headH = o.header && o.header !== 'none' ? 70 : 0;
    var showRule = o.showHeadline && o.headline && o.showName && o.name;

    ctx.font = 'italic 500 32px "Cormorant Garamond"';
    var msgSize = 32, msgLines = null;
    if (o.showMessage && o.message) {
      while (!(msgLines = wrap(ctx, o.message, 660, 2)) && msgSize > 20) { msgSize -= 1; ctx.font = font('italic 500 {s}px "Cormorant Garamond"', msgSize); }
      if (!msgLines) msgLines = wrap(ctx, o.message, 660, 99).slice(0, 2);
    }

    var items = stack([
      headH && { h: headH, gap: 110, draw: function (t) { ORNAMENTS[o.header](ctx, cx, t + headH / 2, c.accent); } },
      o.showNumber && o.number && (function () {
        // measure the real glyph height (old-style figures like 5 and 9 drop below the line)
        var s = fitSize(ctx, o.number, numf.css, 400 * numf.scale, maxW);
        ctx.font = font(numf.css, s);
        var m = ctx.measureText(o.number);
        var asc = m.actualBoundingBoxAscent || s * 0.7, desc = m.actualBoundingBoxDescent || 0;
        return { h: asc + desc, gap: 96, draw: function (t) {
          ctx.font = font(numf.css, s); ctx.fillStyle = c.ink; centred(ctx, o.number, cx, t + asc);
        } };
      })(),
      o.showHeadline && o.headline && { h: 34, gap: showRule ? 30 : 60, draw: function (t) {
        var txt = o.headline.toUpperCase();
        var s = fitSize(ctx, txt, '500 {s}px "Jost"', 34, 800, 0.34);
        ctx.font = font('500 {s}px "Jost"', s); ctx.fillStyle = c.ink; centred(ctx, txt, cx, t + 32, s * 0.34);
      } },
      showRule && { h: 2, gap: 40, draw: function (t) { rule(ctx, t + 1, c.accent); } },
      o.showName && o.name && { h: 100, gap: 64, draw: function (t) {
        var s = fitSize(ctx, o.name, nf.css, 104 * nf.scale, maxW);
        ctx.font = font(nf.css, s); ctx.fillStyle = c.name; centred(ctx, o.name, cx, t + 76);
      } },
      o.showDetails && o.details && { h: 26, gap: 52, draw: function (t) {
        var txt = o.details.toUpperCase();
        var s = fitSize(ctx, txt, '500 {s}px "Cormorant Garamond"', 27, 760, 0.2);
        ctx.font = font('500 {s}px "Cormorant Garamond"', s); ctx.fillStyle = c.muted; centred(ctx, txt, cx, t + 22, s * 0.2);
      } },
      msgLines && { h: msgLines.length * msgSize * 1.36, gap: 0, draw: function (t) {
        ctx.font = font('italic 500 {s}px "Cormorant Garamond"', msgSize); ctx.fillStyle = c.muted;
        msgLines.forEach(function (l, i) { centred(ctx, l, cx, t + msgSize + i * msgSize * 1.36); });
      } }
    ], PAGE_H / 2 + 10);
    items.forEach(function (it) { it.draw(it.top); });
  }

  function names(ctx, o, p) {
    var c = p.c, cx = PAGE_W / 2, maxW = 740;
    var nf = NAME_FONTS[o.nameFont] || NAME_FONTS['Great Vibes'];
    var headH = o.header && o.header !== 'none' ? 96 : 0;
    var hasNames = (o.name1 || o.name2);
    var lower = (o.showLine && o.line) || (o.showPlace && o.place);
    var nameItem = function (txt) {
      return txt && { h: 150, gap: 6, draw: function (t) {
        var s = fitSize(ctx, txt, nf.css, 158 * nf.scale, maxW);
        ctx.font = font(nf.css, s); ctx.fillStyle = c.ink; centred(ctx, txt, cx, t + 118);
      } };
    };
    var items = [
      headH && { h: headH, gap: 64, draw: function (t) { ORNAMENTS[o.header](ctx, cx, t + headH / 2, c.accent); } },
      nameItem(o.name1),
      o.showJoiner && o.joiner && o.name1 && o.name2 && { h: 70, gap: 6, draw: function (t) {
        var s = fitSize(ctx, o.joiner, 'italic 500 {s}px "Cormorant Garamond"', 76, 400);
        ctx.font = font('italic 500 {s}px "Cormorant Garamond"', s); ctx.fillStyle = c.name; centred(ctx, o.joiner, cx, t + 56);
      } },
      nameItem(o.name2)
    ];
    // the gap after the last name is where the rule and lower lines start
    var last = items.filter(Boolean).pop();
    if (last && hasNames) last.gap = lower ? 74 : 0;
    if (hasNames && lower) items.push({ h: 2, gap: 56, draw: function (t) { rule(ctx, t + 1, c.accent); } });
    if (o.showLine && o.line) items.push({ h: 32, gap: 26, draw: function (t) {
      var txt = o.line.toUpperCase();
      var s = fitSize(ctx, txt, '500 {s}px "Jost"', 32, 760, 0.34);
      ctx.font = font('500 {s}px "Jost"', s); ctx.fillStyle = c.ink; centred(ctx, txt, cx, t + 30, s * 0.34);
    } });
    if (o.showPlace && o.place) items.push({ h: 28, gap: 0, draw: function (t) {
      var s = fitSize(ctx, o.place, 'italic 500 {s}px "Cormorant Garamond"', 29, 760);
      ctx.font = font('italic 500 {s}px "Cormorant Garamond"', s); ctx.fillStyle = c.muted; centred(ctx, o.place, cx, t + 24);
    } });
    stack(items, PAGE_H / 2 + 6).forEach(function (it) { it.draw(it.top); });
  }

  /* options: { kind: 'milestone' | 'names', background, accentColor, header, border, nameFont,
       milestone: number, numberFont, headline, name, details, message, showNumber, showHeadline, showName, showDetails, showMessage
       names: name1, joiner, name2, line, place, showJoiner, showLine, showPlace } */
  function draw(canvas, o) {
    var ctx = canvas.getContext('2d');
    var k = canvas.width / PAGE_W;
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.setTransform(k, 0, 0, k, 0, 0);
    ctx.textBaseline = 'alphabetic';
    var p = palette(o);
    background(ctx, o, p);
    if (o.kind === 'names') names(ctx, o, p); else milestone(ctx, o, p);
    ctx.setTransform(1, 0, 0, 1, 0, 0);
  }

  // With a design, loads only the fonts it uses (the base serif and sans, plus its name and number
  // lettering). Without one (print tools), loads them all.
  var BASE_FONTS = ['500 40px "Cormorant Garamond"', 'italic 500 40px "Cormorant Garamond"', '400 40px "Jost"', '500 40px "Jost"', '300 40px "Jost"'];
  function loadFonts(o) {
    if (!document.fonts) return Promise.resolve();
    var list = FONTS;
    if (o) {
      var nf = NAME_FONTS[o.nameFont] || NAME_FONTS['Great Vibes'];
      var numf = NUMBER_FONTS[o.numberFont] || NUMBER_FONTS['Cormorant Garamond'];
      list = BASE_FONTS.concat([font(nf.css, 40), font(numf.css, 40)]);
    }
    // Urdu needs sample Urdu letters, or the browser only fetches the font's Latin part
    return Promise.all(list.map(function (f) { return document.fonts.load(f, /Nastaliq/.test(f) ? 'اردو ابجد' : undefined).catch(function () {}); }));
  }

  // Draws one top design on a small canvas (used for the option buttons)
  function drawOrnament(canvas, key, color) {
    var ctx = canvas.getContext('2d');
    ctx.setTransform(1, 0, 0, 1, 0, 0); ctx.clearRect(0, 0, canvas.width, canvas.height);
    if (!ORNAMENTS[key] || key === 'none') return;
    var k = canvas.width / 260;
    ctx.setTransform(k, 0, 0, k, canvas.width / 2, canvas.height / 2);
    ORNAMENTS[key](ctx, 0, 0, color || '#2B211B');
    ctx.setTransform(1, 0, 0, 1, 0, 0);
  }

  window.OraTextPoster = {
    PAGE_W: PAGE_W, PAGE_H: PAGE_H,
    BACKGROUNDS: BACKGROUNDS, HEADERS: HEADERS, NAME_FONTS: NAME_FONTS, NUMBER_FONTS: NUMBER_FONTS,
    draw: draw, drawOrnament: drawOrnament, loadFonts: loadFonts,
    // 300 dpi print sizes (portrait)
    PRINT: { A4: [2480, 3508], A3: [3508, 4961], A2: [4961, 7016] }
  };
})();
